import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "helpers/utils";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const buttonVariants = {
  initial: (isSelected) => ({
    gap: isSelected ? ".5rem" : 0,
    paddingLeft: isSelected ? "1rem" : ".5rem",
    paddingRight: isSelected ? "1rem" : ".5rem",
  }),
  animate: (isSelected) => ({
    gap: isSelected ? ".5rem" : 0,
    paddingLeft: isSelected ? "1rem" : ".5rem",
    paddingRight: isSelected ? "1rem" : ".5rem",
  }),
};

const transition = { type: "spring", bounce: 0, duration: 0.4 };

export default function ExpandableTabs({ tabs, onChange }) {
  const [selectedId, setSelectedId] = useState(null);

  const { t } = useTranslation();
  const { pathname } = useLocation();

  const handleSelect = (id, item) => {
    setSelectedId(id);
    onChange?.(item.url);
  };

  useEffect(() => {
    const matchingTab = tabs.find((tab) => tab.url === pathname);
    if (matchingTab) {
      setSelectedId(matchingTab.id);
    }
  }, [pathname, tabs]);

  return (
    <div
      className="flex flex-wrap justify-between items-center rounded-full border bg-dark shadow-sm"
    >
      <div
        className={cn(
          "flex flex-wrap justify-between items-center p-2 w-full",
          {
            "pl-5 transition-all duration-100": selectedId !== tabs[0]?.id,
            "pr-5 transition-all duration-100": selectedId !== tabs[tabs.length - 1]?.id,
          }
        )}
      >
        {tabs.map((tab) => {
          const isTabSelected = selectedId === tab.id;
          return (
            <motion.button
              key={tab.id}
              variants={buttonVariants}
              initial="initial"
              animate="animate"
              custom={isTabSelected}
              transition={transition}
              onClick={() => handleSelect(tab.id, tab)}
              className={cn(
                "relative flex items-center rounded-full px-4 py-4 text-sm font-medium transition-colors duration-300",
                {
                  "bg-[#363636] text-white": isTabSelected,
                  "text-[#A6A6A6]": !isTabSelected,
                }
              )}
            >
              <Icon icon={tab.icon} width="25" />
              {isTabSelected && (
                <motion.span
                  layoutId="tab-title"
                  className="overflow-hidden whitespace-nowrap ml-2"
                  transition={transition}
                >
                  {t("menu." + tab.title)}
                </motion.span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
